/* ================================================================
 * Tour stop · The canonical tick order, fixed dt, hash, and events
 * ----------------------------------------------------------------
 * See docs/code-roadmap.md §5.1 for the surrounding narrative.
 *
 * This file is the simulation orchestrator. Everything the
 * client renders is the consequence of `tickMatch(state, inputs)`
 * running 60 times per second of simulated time. Three load-bearing
 * ideas live here; understand them before changing anything.
 *
 * 1) The tick ORDER is canonical.
 *    Each step inside `tickMatch` runs in a specific sequence: apply
 *    inputs → integrate positions → resolve boards → possession →
 *    goalie FSM → goalie saves → puck integration → goal detection
 *    → clock. The order matters; swapping two steps changes
 *    behaviour. AGENTS.md says: "Changes [to the tick order] require
 *    updating docs/simulation-invariants.md in the same PR." The
 *    golden-hash tests will fail loudly if you don't.
 *
 * 2) The dt is FIXED at 1/60 second.
 *    Variable-dt physics would destroy reproducibility (a longer dt
 *    on one machine gives a different outcome). The accumulator in
 *    packages/client/src/app/App.ts (tour stop #9) is what makes a
 *    fixed sim dt coexist with a variable display rate. There is
 *    only one constant for this: `1 / 60`. Don't introduce a second.
 *
 * 3) `state` is mutated IN PLACE; everything else is pure.
 *    AGENTS.md: "Every public sim function is pure relative to its
 *    arguments. Mutating MatchState in place is allowed; mutating
 *    arguments other than `state` is not." The `inputs` parameter
 *    is `ReadonlyArray<PlayerInput>` to enforce this at compile time.
 *
 * Two more details worth pointing out:
 *
 *  - Events ACCUMULATE across ticks. `tickMatch` only appends to
 *    `state.events`; it never clears. The consumer (renderer / audio
 *    dispatcher) reads them via the snapshot and then calls
 *    `drainEvents(state)` once per frame to clear the live list. This
 *    means a single render frame that drives MULTIPLE sim ticks (via
 *    the client accumulator) sees every event from every tick. If you
 *    add a new sim system that emits events, push to `state.events` —
 *    drainage is the caller's problem.
 *
 *  - The final steps quantise every position and velocity via `qVec`
 *    / `qVelVec`, then mix the entire state into a 32-bit hash. The
 *    hash is what the golden-hash tests pin against. Any new field
 *    that affects gameplay MUST be added to `computeHash` in core/state-hash.ts;
 *    AGENTS.md makes this a hard rule.
 *
 * If you're adding a new sim system, see
 * .claude/skills/adding-a-sim-system/SKILL.md for the canonical
 * workflow, including which step of `tickMatch` to wire into.
 * ================================================================ */

import {
  InputButton,
  type MatchEvent,
  type PlayerId,
  type PlayerInput,
} from "@arcade-hockey/shared";
import {
  resolvePlayerVsBoards,
  resolvePlayerVsGoals,
  resolveSkaterVsGoalie,
} from "../collision/index.js";
import { GOALIE_SAVE_RADIUS, resolveGoalieSaves } from "../goalie/save-volumes.js";
import { tickGoalie } from "../goalie/state-machine.js";
import { tickPlayerActions } from "../hockey/action-timer.js";
import { applyAutoSwitchOnPass, applyAutoSwitchOnPickup } from "../hockey/auto-switch.js";
import { applyBodyChecks } from "../hockey/body-check.js";
import { tickGoalDetection } from "../hockey/goal.js";
import { applyPassInput } from "../hockey/pass.js";
import { resolvePlayerVsPlayer } from "../hockey/player-separation.js";
import { tickPossession } from "../hockey/possession.js";
import { integrateLoosePuck } from "../hockey/puck-motion.js";
import { applyPokeStrip, applyStickLiftStrip } from "../hockey/puck-strip.js";
import { applyShootInput } from "../hockey/shoot.js";
import { applySkating } from "../hockey/skating.js";
import { applyManualSwitch } from "../hockey/switching.js";
import { qVecInPlace, qVelVecInPlace } from "../math/Quantize.js";
import { createRng } from "../math/Random.js";
import { applyAutoPullGoalie } from "../rules/auto-pull.js";
import { collectFaceoffIntents, resolveFaceoffPop } from "../rules/faceoff.js";
import { tickGoalieFreeze } from "../rules/goalie-freeze.js";
import { tickIcing } from "../rules/icing.js";
import { tickOffside } from "../rules/offside.js";
import { enterOvertime, isLivePlayPhase } from "../rules/overtime.js";
import { tickPuckOutOfPlay } from "../rules/puck-out-of-play.js";
import { enterShootout, tickShootout } from "../rules/shootout.js";
import { layoutFaceoffPositions } from "../state/faceoff-spots.js";
import {
  type HumanSeatsConfig,
  type RosterSizes,
  createInitialControl,
  createInitialGoalies,
  createInitialPlayers,
  resetPlayersToSpawn,
} from "../state/initial.js";
import { tuning } from "../state/tuning.js";
import type {
  FaceoffIntentTracker,
  GoalieState,
  MatchClock,
  MatchSnapshot,
  MatchState,
  PlayerState,
  PuckState,
  ReplaySetup,
  RinkConsts,
  RulesState,
  ScoreState,
  SetupGoalie,
  SetupPlayer,
  TeamControlState,
} from "../state/types.js";
import { computeHash } from "./state-hash.js";

export type CreateMatchOptions = {
  seed: number;
  // Override which seats are human-controlled. When omitted, defaults to the
  // legacy Player-vs-CPU layout: home P1 = home-1, away fully bot. Pass
  // `{ home: { P1: null }, away: { P1: null } }` for a CPU-vs-CPU match where
  // tickBots drives every skater.
  humanSeats?: HumanSeatsConfig;
  // Per-team skater count 0–5 (goalies always present). Omitting defaults
  // to the canonical 5v5 roster — keeps Phase D goldens pinned.
  rosterSizes?: RosterSizes;
};

export function createMatch(opts: CreateMatchOptions): MatchState {
  const clock: MatchClock = {
    periodIndex: 0,
    remainingTicks: tuning.clock.faceoffPauseTicks,
    phase: "Faceoff",
  };

  const rink: RinkConsts = {
    length: tuning.rink.length,
    width: tuning.rink.width,
    cornerRadius: tuning.rink.cornerRadius,
    goalMouthHalfWidth: tuning.rink.goalMouthHalfWidth,
    goalDepth: tuning.rink.goalDepth,
  };

  const puck: PuckState = {
    pos: { x: 0, y: 0 },
    vel: { x: 0, y: 0 },
    mode: "Loose",
    ownerId: null,
    lastTouchedBy: null,
    noRecaptureUntilTick: 0,
    opponentLockoutPlayerId: null,
    opponentLockoutUntilTick: 0,
    faceoffCenterLockoutUntilTick: 0,
  };

  const state: MatchState = {
    tick: 0,
    clock,
    rng: createRng(opts.seed),
    score: { home: 0, away: 0 },
    rink,
    puck,
    players: createInitialPlayers(opts.rosterSizes),
    goalies: createInitialGoalies(),
    control: createInitialControl(opts.humanSeats),
    // Per-rule scratch — see RulesState in state/types.ts. Empty at match
    // start; rule slices populate their sub-fields lazily as triggers fire.
    rules: {},
    events: [],
    hash: 0,
    playersById: new Map(),
  };

  // Canonical sort order, established once. All in-tick consumers (hash, body
  // checks, player separation, possession, puck-strip, switching) rely on this
  // ordering and must NOT re-sort per tick. See AGENTS.md "Tick discipline".
  state.players.sort((a, b) => a.id.localeCompare(b.id));
  state.goalies.sort((a, b) => a.id.localeCompare(b.id));
  state.control.sort((a, b) => a.teamId.localeCompare(b.teamId));
  rebuildPlayersById(state);

  state.hash = computeHash(state);
  return state;
}

/**
 * Repopulate the PlayerId → PlayerState lookup cache. Call after any change to
 * `state.players` (additions, removals). In Phase C the roster is fixed, so
 * this is invoked exactly once from createMatch.
 */
export function rebuildPlayersById(state: MatchState): void {
  state.playersById.clear();
  for (const p of state.players) state.playersById.set(p.id, p);
}

/**
 * O(1) player lookup. Use this in hot paths instead of `state.players.find`.
 * Returns null if `id` is null or not present.
 */
export function getPlayerById(state: MatchState, id: PlayerId | null): PlayerState | null {
  if (id === null) return null;
  return state.playersById.get(id) ?? null;
}

/**
 * Apply a contrived starting state to a freshly-created match. Used by demo
 * and test replays that need the puck and players placed somewhere specific
 * before tick 0, skipping the faceoff dance.
 *
 * Component-wise copies into existing `pos` / `vel` / `faceDir` etc. so we
 * never reassign a long-lived Vec2 reference (the renderer and snapshot copy
 * pipeline hold those refs).
 *
 * Throws on unknown player/goalie/team IDs — caller (replay loader, demo
 * script) is the source of those names and should be precise.
 */
export function applySetup(state: MatchState, setup: ReplaySetup): void {
  if (setup.clock !== undefined) {
    if (setup.clock.phase !== undefined) state.clock.phase = setup.clock.phase;
    if (setup.clock.remainingTicks !== undefined) {
      state.clock.remainingTicks = setup.clock.remainingTicks;
    }
    if (setup.clock.periodIndex !== undefined) {
      state.clock.periodIndex = setup.clock.periodIndex;
    }
    if (setup.clock.nextPlayPhase !== undefined) {
      state.clock.nextPlayPhase = setup.clock.nextPlayPhase;
    }
  }
  if (setup.score !== undefined) {
    if (setup.score.home !== undefined) state.score.home = setup.score.home;
    if (setup.score.away !== undefined) state.score.away = setup.score.away;
  }
  if (setup.puck !== undefined) {
    const sp = setup.puck;
    if (sp.pos !== undefined) {
      state.puck.pos.x = sp.pos.x;
      state.puck.pos.y = sp.pos.y;
    }
    if (sp.vel !== undefined) {
      state.puck.vel.x = sp.vel.x;
      state.puck.vel.y = sp.vel.y;
    }
    if (sp.mode !== undefined) state.puck.mode = sp.mode;
    if (sp.ownerId !== undefined) state.puck.ownerId = sp.ownerId;
    if (sp.lastTouchedBy !== undefined) state.puck.lastTouchedBy = sp.lastTouchedBy;
    if (sp.noRecaptureUntilTick !== undefined) {
      state.puck.noRecaptureUntilTick = sp.noRecaptureUntilTick;
    }
    if (sp.opponentLockoutPlayerId !== undefined) {
      state.puck.opponentLockoutPlayerId = sp.opponentLockoutPlayerId;
    }
    if (sp.opponentLockoutUntilTick !== undefined) {
      state.puck.opponentLockoutUntilTick = sp.opponentLockoutUntilTick;
    }
    if (sp.faceoffCenterLockoutUntilTick !== undefined) {
      state.puck.faceoffCenterLockoutUntilTick = sp.faceoffCenterLockoutUntilTick;
    }
  }
  if (setup.players !== undefined) {
    for (const sp of setup.players) {
      const target = state.playersById.get(sp.id);
      if (target === undefined) {
        throw new Error(`applySetup: unknown player id "${sp.id}"`);
      }
      applyPlayerOverride(target, sp);
    }
  }
  if (setup.goalies !== undefined) {
    for (const sg of setup.goalies) {
      const target = state.goalies.find((g) => g.id === sg.id);
      if (target === undefined) {
        throw new Error(`applySetup: unknown goalie id "${sg.id}"`);
      }
      applyGoalieOverride(target, sg);
    }
  }
  if (setup.control !== undefined) {
    for (const sc of setup.control) {
      const target = state.control.find((c) => c.teamId === sc.teamId);
      if (target === undefined) {
        throw new Error(`applySetup: unknown team "${sc.teamId}"`);
      }
      if (sc.controlledByHuman !== undefined) {
        if (sc.controlledByHuman.P1 !== undefined) {
          target.controlledByHuman.P1 = sc.controlledByHuman.P1;
        }
        if (sc.controlledByHuman.P2 !== undefined) {
          target.controlledByHuman.P2 = sc.controlledByHuman.P2;
        }
      }
    }
  }
  if (setup.rules !== undefined) {
    applyRulesOverride(state.rules, setup.rules);
  }
  state.hash = computeHash(state);
}

function applyRulesOverride(target: RulesState, source: Partial<RulesState>): void {
  // Per-rule override merge. `Partial<RulesState>` means each field is
  // independently optional in the setup — only present fields override.
  if (source.nextFaceoffSpot !== undefined) {
    target.nextFaceoffSpot = source.nextFaceoffSpot;
  }
  if (source.faceoffIntents !== undefined) {
    target.faceoffIntents = {};
    if (source.faceoffIntents.home !== undefined) {
      target.faceoffIntents.home = cloneTracker(source.faceoffIntents.home);
    }
    if (source.faceoffIntents.away !== undefined) {
      target.faceoffIntents.away = cloneTracker(source.faceoffIntents.away);
    }
  }
  if (source.offside !== undefined) {
    target.offside = {
      team: source.offside.team,
      potentialIds: source.offside.potentialIds.slice(),
      puckEnterX: source.offside.puckEnterX,
      puckEnterY: source.offside.puckEnterY,
    };
  }
  if (source.icing !== undefined) {
    target.icing = {
      team: source.icing.team,
      shooterId: source.icing.shooterId,
      shotFromY: source.icing.shotFromY,
    };
  }
  if (source.puckOutOfPlay !== undefined) {
    target.puckOutOfPlay = { ticks: source.puckOutOfPlay.ticks };
  }
  if (source.shootout !== undefined) {
    target.shootout = {
      round: source.shootout.round,
      shooterTeam: source.shootout.shooterTeam,
      shooterId: source.shootout.shooterId,
      subPhase: source.shootout.subPhase,
      subPhaseEndTick: source.shootout.subPhaseEndTick,
      lastResult: source.shootout.lastResult,
      score: { home: source.shootout.score.home, away: source.shootout.score.away },
      attempts: { home: source.shootout.attempts.home, away: source.shootout.attempts.away },
      shotTaken: source.shootout.shotTaken,
      shotTakenAtTick: source.shootout.shotTakenAtTick,
      maxPuckForward: source.shootout.maxPuckForward,
    };
  }
}

function applyPlayerOverride(target: PlayerState, sp: SetupPlayer): void {
  if (sp.pos !== undefined) {
    target.pos.x = sp.pos.x;
    target.pos.y = sp.pos.y;
  }
  if (sp.vel !== undefined) {
    target.vel.x = sp.vel.x;
    target.vel.y = sp.vel.y;
  }
  if (sp.moveDir !== undefined) {
    target.moveDir.x = sp.moveDir.x;
    target.moveDir.y = sp.moveDir.y;
  }
  if (sp.faceDir !== undefined) {
    target.faceDir.x = sp.faceDir.x;
    target.faceDir.y = sp.faceDir.y;
  }
  if (sp.stickDir !== undefined) {
    target.stickDir.x = sp.stickDir.x;
    target.stickDir.y = sp.stickDir.y;
  }
  if (sp.action !== undefined) target.action = sp.action;
  if (sp.actionTimer !== undefined) target.actionTimer = sp.actionTimer;
  if (sp.stunTicks !== undefined) target.stunTicks = sp.stunTicks;
  if (sp.recoveryTicks !== undefined) target.recoveryTicks = sp.recoveryTicks;
  if (sp.stamina !== undefined) target.stamina = sp.stamina;
  if (sp.hasPuck !== undefined) target.hasPuck = sp.hasPuck;
  if (sp.possessionQuality !== undefined) target.possessionQuality = sp.possessionQuality;
  if (sp.shootHoldTicks !== undefined) target.shootHoldTicks = sp.shootHoldTicks;
  if (sp.passHoldTicks !== undefined) target.passHoldTicks = sp.passHoldTicks;
  if (sp.receivedPassFromTick !== undefined) target.receivedPassFromTick = sp.receivedPassFromTick;
  if (sp.isProtecting !== undefined) target.isProtecting = sp.isProtecting;
  if (sp.benched !== undefined) target.benched = sp.benched;
}

function applyGoalieOverride(target: GoalieState, sg: SetupGoalie): void {
  if (sg.pos !== undefined) {
    target.pos.x = sg.pos.x;
    target.pos.y = sg.pos.y;
  }
  if (sg.vel !== undefined) {
    target.vel.x = sg.vel.x;
    target.vel.y = sg.vel.y;
  }
  if (sg.facing !== undefined) {
    target.facing.x = sg.facing.x;
    target.facing.y = sg.facing.y;
  }
  if (sg.mode !== undefined) target.mode = sg.mode;
  if (sg.modeTimer !== undefined) target.modeTimer = sg.modeTimer;
  if (sg.pulled !== undefined) target.pulled = sg.pulled;
}

const SPRINT_TURBO_THRESHOLD = 0.5;

function findControlledPlayer(state: MatchState, input: PlayerInput): PlayerState | null {
  if (input.playerId !== null) {
    return getPlayerById(state, input.playerId);
  }
  const seat = input.seatId;
  if (seat !== "P1" && seat !== "P2") return null;
  for (const team of state.control) {
    const pid = team.controlledByHuman[seat];
    if (pid !== null) {
      return getPlayerById(state, pid);
    }
  }
  return null;
}

function updateProtectingFlags(state: MatchState, inputs: ReadonlyArray<PlayerInput>): void {
  for (const player of state.players) {
    player.isProtecting = false;
  }
  for (const input of inputs) {
    const player = findControlledPlayer(state, input);
    if (player === null) continue;
    if (player.benched) continue;
    if (!player.hasPuck) continue;
    if ((input.buttons & InputButton.Context) === 0) continue;
    if (player.stamina < tuning.player.staminaLockoutThreshold) continue;
    player.isProtecting = true;
  }
}

function updateManualGoalieFlags(state: MatchState, inputs: ReadonlyArray<PlayerInput>): void {
  for (const team of state.control) {
    team.manualGoalieSeat = null;
  }
  for (const input of inputs) {
    if ((input.buttons & InputButton.Goalie) === 0) continue;
    const seat = input.seatId;
    if (seat !== "P1" && seat !== "P2") continue;
    const team = state.control.find((t) => t.controlledByHuman[seat] !== null);
    if (team === undefined) continue;
    const goalie = state.goalies.find((g) => g.teamId === team.teamId);
    if (goalie?.pulled) continue;
    team.manualGoalieSeat = input.seatId;
  }
}

function updateGoaliePullToggle(state: MatchState, inputs: ReadonlyArray<PlayerInput>): void {
  for (const team of state.control) {
    let heldThisTick = false;
    for (const input of inputs) {
      const seat = input.seatId;
      if (seat !== "P1" && seat !== "P2") continue;
      if (team.controlledByHuman[seat] === null) continue;
      if ((input.buttons & InputButton.GoaliePullToggle) !== 0) {
        heldThisTick = true;
        break;
      }
    }
    const wasHeld = team.goaliePullToggleHeldLastTick;
    if (heldThisTick && !wasHeld) {
      const goalie = state.goalies.find((g) => g.teamId === team.teamId);
      if (goalie !== undefined) {
        goalie.pulled = !goalie.pulled;
        if (goalie.pulled) {
          const halfL = tuning.rink.length / 2;
          const sign = goalie.teamId === "home" ? -1 : 1;
          goalie.pos = { x: sign * (halfL + 0.5), y: 0 };
          goalie.vel = { x: 0, y: 0 };
        }
      }
    }
    team.goaliePullToggleHeldLastTick = heldThisTick;
  }
}

function findInputForSeat(
  inputs: ReadonlyArray<PlayerInput>,
  seatId: string,
): { moveX: number; moveY: number } | null {
  for (const input of inputs) {
    if (input.seatId === seatId) {
      return { moveX: input.moveX, moveY: input.moveY };
    }
  }
  return null;
}

/**
 * Per-tick simulation step. The order of operations below IS the rules
 * system — each numbered step mutates a specific slice of MatchState and
 * subsequent steps can read those mutations. Future rule slices wire in
 * at named steps (see `docs/sim-architecture.md` for the per-step
 * description and the rule-addition recipe).
 *
 * Step numbers are stable: docs reference them by number, so when adding
 * a new step prefer inserting at the end OR using a sub-letter (e.g. 9a).
 */
export function tickMatch(state: MatchState, inputs: ReadonlyArray<PlayerInput>): MatchState {
  // Step 1 — Player action timers. Decrement stunTicks/recoveryTicks;
  // transition Stunned → Idle and RecoverCheck → Idle when timers hit 0.
  tickPlayerActions(state.players);

  // Step 2 — Protect-puck flags. For every input whose controlled player
  // is the carrier and holds Context, set `player.isProtecting = true`.
  updateProtectingFlags(state, inputs);

  // Step 3 — Manual-goalie flags. For every input holding Goalie, mark
  // that seat as the team's current manual goalie controller.
  updateManualGoalieFlags(state, inputs);

  // Step 4 — Goalie-pull toggle. Edge-detect GoaliePullToggle button
  // presses and flip the team's goalie pulled state.
  updateGoaliePullToggle(state, inputs);

  // Step 4a' — Slice 10 auto-pull. Period 3, last 60 seconds, trailing
  // team within 2 goals → pull goalie + switch to aggressive. Runs
  // every tick but no-ops once already pulled.
  applyAutoPullGoalie(state);

  // Step 4a — Faceoff intent collection. Only runs during the Faceoff
  // phase. Each centre's input.moveX is sampled into
  // state.rules.faceoffIntents — last writer wins. Resolved at the
  // Faceoff → Play transition in Step 18.
  collectFaceoffIntents(state, inputs);

  // Capture pre-integration puck position for goal-line crossing detection
  // (Step 17) and the pickup-auto-switch (Step 12). Stored as scalar locals
  // — not a fresh Vec2 — to keep tickMatch zero-alloc.
  const prevPuckX = state.puck.pos.x;
  const prevPuckY = state.puck.pos.y;
  const prevOwnerId = state.puck.ownerId;

  // Slice 8 — both regulation Play and Overtime are "live play" phases.
  // Skating, possession, puck integration, and the rules that fire during
  // active play (offside, goalie freeze, puck-out-of-play) gate on this
  // flag. Icing is the exception — it stays disabled during OT (per
  // real-hockey rule) by an inner check in `rules/icing.ts`.
  const isLive = isLivePlayPhase(state.clock.phase);

  // Step 5 — Per-input pass. For each controlled actor, apply (in order):
  //   strips (poke + stick-lift), manual seat switching, skating
  //   acceleration, stickDir update, shoot/pass intent.
  // Emits ShotReleased / PassReleased events on release.
  for (const input of inputs) {
    if (!isLive) continue;
    applyPokeStrip(state, input);
    applyStickLiftStrip(state, input);
    applyManualSwitch(state, input);
    const player = findControlledPlayer(state, input);
    if (player === null) continue;
    if (player.benched) continue;
    const turbo = input.sprint >= SPRINT_TURBO_THRESHOLD;
    // moveMag computed once per controlled-player tick; shared with applySkating
    // and the stickDir update. Math.sqrt matches Math.hypot bit-for-bit in the
    // bounded input range we exercise.
    const moveMag = Math.sqrt(input.moveX * input.moveX + input.moveY * input.moveY);
    applySkating(player, { moveX: input.moveX, moveY: input.moveY, moveMag, turbo });

    if (moveMag > 0.2) {
      player.stickDir.x = input.moveX / moveMag;
      player.stickDir.y = input.moveY / moveMag;
    }
    player.faceDir.x = player.stickDir.x;
    player.faceDir.y = player.stickDir.y;

    const shotEvent = applyShootInput(player, state.puck, input, state.tick, state.rng);
    if (shotEvent !== null) state.events.push(shotEvent);
    const passEvent = applyPassInput(player, state.puck, input, state.tick);
    if (passEvent !== null) state.events.push(passEvent);
  }

  // Step 6 — Auto-switch on pass. If P1 just fired a pass at a teammate,
  // hand seat control to the receiver pre-arrival so the human's next
  // input goes to the right body.
  applyAutoSwitchOnPass(state);

  // Step 7 — Integrate player positions. pos += vel * dt for all skaters.
  // Slice 8 — benched skaters sit at the bench off the +y board; skip
  // them so their position stays pinned.
  const dt = 1 / 60;
  for (const player of state.players) {
    if (player.benched) continue;
    player.pos.x += player.vel.x * dt;
    player.pos.y += player.vel.y * dt;
  }

  // Step 8 — Skater-vs-environment collisions. Three sub-resolvers, in
  // order: boards (rounded rink boundary), goal frames (posts + back +
  // sides), goalie bodies (so a carrier can't dribble through the crease).
  // Each mutates player.pos + player.vel along the contact normal.
  // Benched skaters skip — they're off-ice intentionally.
  for (const player of state.players) {
    if (player.benched) continue;
    resolvePlayerVsBoards(player);
    resolvePlayerVsGoals(player);
    for (const goalie of state.goalies) {
      if (goalie.pulled) continue;
      resolveSkaterVsGoalie(player, goalie.pos.x, goalie.pos.y, GOALIE_SAVE_RADIUS);
    }
  }

  // Step 9 — Body checks. Detect overlap+sprint pairs from THIS tick
  // before player-vs-player separation washes the overlap out. Emits
  // CheckLanded; pops the puck loose and stuns the victim.
  applyBodyChecks(state, inputs);

  // Step 10 — Player-vs-player soft separation. Push overlapping skaters
  // apart along the contact normal; does NOT modify velocities. Run AFTER
  // body checks so the overlap signal isn't lost.
  resolvePlayerVsPlayer(state.players);

  // Step 11 — Possession update. For a Controlled puck, hard-pin the
  // puck to the carrier's stick anchor and decay quality. For a Loose
  // puck, the first player in stick zone claims it. Gated on Play —
  // during Faceoff the centers spawn 1 m apart, player separation pushes
  // them to ±0.65 m, and the puck (at center) would otherwise land
  // inside the away C's stick zone before the puck has been "dropped".
  if (isLive) {
    tickPossession({
      players: state.players,
      playersById: state.playersById,
      puck: state.puck,
      currentTick: state.tick,
    });
    // Step 12 — Auto-switch on pickup. If a new carrier just took
    // possession, hand seat control to them.
    applyAutoSwitchOnPickup(state, prevOwnerId);
  }

  // Step 13 — Tick goalies. Either manual (if a P1/P2 seat holds Goalie)
  // or the FSM that tracks the puck along the goal-line arc.
  for (const goalie of state.goalies) {
    const team = state.control.find((t) => t.teamId === goalie.teamId);
    const seat = team?.manualGoalieSeat ?? null;
    const manualInput = seat === null ? null : findInputForSeat(inputs, seat);
    tickGoalie(goalie, state.puck.pos, manualInput);
  }

  // Step 14 — Puck vs goalie body. Shots that hit the goalie's save
  // volume become saves (emit GoalieSave, set Loose with rebound vel).
  // Passes/Loose pucks deflect off the goalie body without a save event.
  resolveGoalieSaves(state.goalies, state.puck, state.tick, state.events);

  // Step 14a — Goalie freeze. If after the save resolution the puck is
  // slow, near the goalie body, and last-touched by that goalie, she
  // covers it → Stoppage at the end-zone dot on the puck's side.
  // Sits between save resolution (which marks lastTouchedBy and reflects
  // pass-mode contacts) and puck integration so a same-tick "slow pass
  // deflects off body, gets frozen" sequence resolves cleanly.
  if (isLive) tickGoalieFreeze(state);

  // Step 15 — Integrate loose puck. Swept circle vs the segment list
  // (boards + goal-frame top/back/bottom for each goal). Reflects with
  // restitution + tangential damping; emits PuckBoardHit on each bounce.
  // Only runs during Play, and only for non-Controlled modes.
  if (
    isLive &&
    (state.puck.mode === "Loose" || state.puck.mode === "Shot" || state.puck.mode === "Pass")
  ) {
    integrateLoosePuck(state.puck, 1 / 60, state.tick, state.events);
  }

  // Step 16 — Puck mode transitions. Pass → Loose once the recapture
  // window expires. Shot → Loose once the puck slows below the
  // "still actively a shot" threshold (so teammates can't grab a
  // mid-air pass).
  if (state.puck.mode === "Pass" && state.tick >= state.puck.noRecaptureUntilTick) {
    state.puck.mode = "Loose";
  }
  if (state.puck.mode === "Shot" && state.tick >= state.puck.noRecaptureUntilTick) {
    // Keep Shot mode active until the puck slows below
    // tuning.shot.activeMinSpeed (rationale documented on the constant).
    const minSpeed = tuning.shot.activeMinSpeed;
    const speedSq = state.puck.vel.x * state.puck.vel.x + state.puck.vel.y * state.puck.vel.y;
    if (speedSq < minSpeed * minSpeed) {
      state.puck.mode = "Loose";
    }
  }

  // Step 16a — Offside detection. Walks the puck's blue-line crossing
  // for this tick. If an attacker (other than the carrier) is already
  // in the OZ when the puck enters, a pending-offside state is set;
  // touching the puck trips the whistle, exiting the OZ "tags up" and
  // clears. Runs BEFORE goal detection so an offside cancels a
  // would-be goal in the same tick.
  tickOffside(state, prevPuckX);

  // Step 16b — Icing detection. Arms on a ShotReleased from the
  // shooter's own half of centre ice; whistles when the armed puck
  // crosses the opposing goal line untouched. Cancellation: any other
  // player touching the puck (lastTouchedBy changes). Before goal
  // detection so an icing cancels a would-be goal in the same tick.
  tickIcing(state, prevPuckX);

  // Step 16c — Puck out of play. Either an escape past the rink
  // boundary (defensive net) or a corner stall — slow, unattended
  // puck in a corner for 3 seconds. Whistles at the nearest faceoff
  // dot. Sits after icing so a same-tick icing whistle takes
  // precedence; sits before goal detection so an escape past the
  // back-of-net can't be mistaken for a goal.
  tickPuckOutOfPlay(state);

  // Step 17 — Goal detection. Tests the line drawn by (prevPuckX,
  // prevPuckY) → current puck pos against each goal line within the
  // mouth y-range. On a hit: increment score, emit GoalScored, transition
  // to GoalCelebration. No-op for Controlled-mode pucks (the puck is
  // hard-pinned to the stick and can teleport via rotation).
  tickGoalDetection(state, prevPuckX, prevPuckY);

  // Step 17a — Shootout sub-phase machine. Sits AFTER goal detection
  // so a same-tick GoalScored event is visible to the shootout's
  // result-watcher. No-op outside the Shootout phase.
  tickShootout(state);

  // Step 18 — Match clock. Tick increment + period/phase transitions
  // (Play → PeriodBreak → Faceoff → Play, or Play → GameOver after the
  // last period).
  state.tick += 1;
  if (state.clock.phase === "Play") {
    if (state.clock.remainingTicks > 0) {
      state.clock.remainingTicks -= 1;
    }
    if (state.clock.remainingTicks <= 0) {
      const periodIndex = state.clock.periodIndex;
      state.events.push({ type: "PeriodEnded", tick: state.tick, periodIndex });
      if (periodIndex < 2) {
        state.clock.phase = "PeriodBreak";
        state.clock.remainingTicks = tuning.clock.periodBreakTicks;
      } else if (state.score.home !== state.score.away) {
        // Period 3 ended with a winner — regulation over.
        state.clock.phase = "GameOver";
        const winner = state.score.home > state.score.away ? "home" : "away";
        state.events.push({ type: "MatchEnded", tick: state.tick, winner });
      } else {
        // Period 3 ended tied — Slice 8 enters 3-on-3 sudden-death OT.
        enterOvertime(state);
      }
    }
  } else if (state.clock.phase === "Overtime") {
    if (state.clock.remainingTicks > 0) {
      state.clock.remainingTicks -= 1;
    }
    if (state.clock.remainingTicks <= 0) {
      // OT expired tied → Slice 9 shootout.
      enterShootout(state);
    }
  } else if (state.clock.phase === "Shootout") {
    // The shootout is driven from `tickShootout` (called below at
    // Step 16d). Nothing to do in the clock branch.
  } else if (state.clock.phase === "PeriodBreak") {
    if (state.clock.remainingTicks > 0) {
      state.clock.remainingTicks -= 1;
    }
    if (state.clock.remainingTicks <= 0) {
      const next = (state.clock.periodIndex + 1) as 0 | 1 | 2;
      state.clock.periodIndex = next;
      state.clock.phase = "Faceoff";
      state.clock.remainingTicks = tuning.clock.faceoffPauseTicks;
      state.puck.pos = { x: 0, y: 0 };
      state.puck.vel = { x: 0, y: 0 };
      state.puck.mode = "Loose";
      state.puck.ownerId = null;
      state.puck.noRecaptureUntilTick = state.tick + tuning.clock.faceoffPauseTicks;
      // Reset every skater to its spawn so the new period opens from a
      // clean faceoff shape, not wherever the buzzer caught them.
      resetPlayersToSpawn(state.players);
      state.events.push({ type: "Faceoff", tick: state.tick, spot: "Center" });
    }
  } else if (state.clock.phase === "Faceoff") {
    if (state.clock.remainingTicks > 0) {
      state.clock.remainingTicks -= 1;
    }
    if (state.clock.remainingTicks <= 0) {
      // Resolve the RPS-style faceoff intents into a puck pop BEFORE
      // flipping to Play. Without intents (both undefined) it pops as
      // hold-vs-hold (sideways toward boards) — see
      // `rules/faceoff.ts:resolveFaceoffPop`.
      resolveFaceoffPop(state);
      // Slice 8 — OT faceoffs route into the Overtime phase with the
      // OT clock, not a regulation period clock. `nextPlayPhase` is
      // set by `enterOvertime` and consumed here.
      const next = state.clock.nextPlayPhase ?? "Play";
      state.clock.phase = next;
      state.clock.remainingTicks =
        next === "Overtime" ? tuning.clock.overtimeTicks : tuning.clock.periodTicks;
      state.clock.nextPlayPhase = undefined;
    }
  } else if (state.clock.phase === "GoalCelebration") {
    if (state.clock.remainingTicks > 0) {
      state.clock.remainingTicks -= 1;
    }
    // tickGoalDetection handles the transition out of GoalCelebration.
  } else if (state.clock.phase === "Stoppage") {
    // Stoppage → Faceoff. The Stoppage window is the "whistle blowing"
    // pause where players haven't moved yet; once it expires, snap
    // every skater to the per-spot faceoff layout (Slice 0 foundation
    // for offside / icing / goalie-freeze / etc.).
    if (state.clock.remainingTicks > 0) {
      state.clock.remainingTicks -= 1;
    }
    if (state.clock.remainingTicks <= 0) {
      const spot = state.rules.nextFaceoffSpot ?? "Center";
      layoutFaceoffPositions(state.players, spot);
      state.clock.phase = "Faceoff";
      state.clock.remainingTicks = tuning.clock.faceoffPauseTicks;
      state.events.push({ type: "Faceoff", tick: state.tick, spot });
      // Keep nextFaceoffSpot set through the Faceoff phase so consumers
      // (renderer overlay, bots) can read it. Clear it on the
      // Faceoff → Play transition below.
    }
  }
  // GameOver: no-op.

  // Once Faceoff expires into Play, clear nextFaceoffSpot — the spot was
  // consumed and the puck is now live. Doing this here (rather than in
  // the Faceoff branch above) keeps the clearing tied to the actual
  // Play transition.
  if (state.clock.phase === "Play" && state.rules.nextFaceoffSpot !== undefined) {
    state.rules.nextFaceoffSpot = undefined;
  }

  // Step 19 — Quantize positions + velocities. Snap every vec to the
  // shared fixed-precision grid so cross-machine determinism survives
  // floating-point drift. Same precision for pos & vel; tuned for
  // typical sim ranges.
  for (const player of state.players) {
    qVecInPlace(player.pos);
    qVelVecInPlace(player.vel);
  }
  qVecInPlace(state.puck.pos);
  qVelVecInPlace(state.puck.vel);
  for (const goalie of state.goalies) {
    qVecInPlace(goalie.pos);
    qVelVecInPlace(goalie.vel);
  }
  // Step 20 — Hash. Mix every gameplay-affecting field into a 32-bit
  // hash for replay verification. See `computeHash` in core/state-hash.ts for what's
  // included; AGENTS.md rule: any new field MUST be mixed in here.
  state.hash = computeHash(state);

  return state;
}

// Hand-rolled mutable snapshot buffer. takeSnapshot mutates this in place and
// returns it as a readonly MatchSnapshot — eliminates the structuredClone of
// MatchState (the single biggest per-frame allocation in the client).
//
// Callers must consume the returned snapshot within a single tick boundary;
// the next takeSnapshot call overwrites its contents. App.ts already does
// exactly one snapshot per render frame.
//
// Phase D-readiness: this is the shape that will become the wire format.

function makePlayerSlot(): PlayerState {
  return {
    id: "" as PlayerId,
    teamId: "home",
    position: "C",
    pos: { x: 0, y: 0 },
    vel: { x: 0, y: 0 },
    moveDir: { x: 0, y: 0 },
    faceDir: { x: 0, y: 0 },
    stickDir: { x: 0, y: 0 },
    action: "Idle",
    actionTimer: 0,
    stunTicks: 0,
    recoveryTicks: 0,
    stamina: 0,
    hasPuck: false,
    possessionQuality: 0,
    shootHoldTicks: 0,
    passHoldTicks: 0,
    receivedPassFromTick: 0,
    isProtecting: false,
    benched: false,
  };
}

function copyPlayerInto(dst: PlayerState, src: PlayerState): void {
  dst.id = src.id;
  dst.teamId = src.teamId;
  dst.position = src.position;
  dst.pos.x = src.pos.x;
  dst.pos.y = src.pos.y;
  dst.vel.x = src.vel.x;
  dst.vel.y = src.vel.y;
  dst.moveDir.x = src.moveDir.x;
  dst.moveDir.y = src.moveDir.y;
  dst.faceDir.x = src.faceDir.x;
  dst.faceDir.y = src.faceDir.y;
  dst.stickDir.x = src.stickDir.x;
  dst.stickDir.y = src.stickDir.y;
  dst.action = src.action;
  dst.actionTimer = src.actionTimer;
  dst.stunTicks = src.stunTicks;
  dst.recoveryTicks = src.recoveryTicks;
  dst.stamina = src.stamina;
  dst.hasPuck = src.hasPuck;
  dst.possessionQuality = src.possessionQuality;
  dst.shootHoldTicks = src.shootHoldTicks;
  dst.passHoldTicks = src.passHoldTicks;
  dst.receivedPassFromTick = src.receivedPassFromTick;
  dst.isProtecting = src.isProtecting;
  dst.benched = src.benched;
}

function makeGoalieSlot(): GoalieState {
  return {
    id: "" as PlayerId,
    teamId: "home",
    pos: { x: 0, y: 0 },
    vel: { x: 0, y: 0 },
    facing: { x: 0, y: 0 },
    mode: "Ready",
    modeTimer: 0,
    pulled: false,
  };
}

function copyGoalieInto(dst: GoalieState, src: GoalieState): void {
  dst.id = src.id;
  dst.teamId = src.teamId;
  dst.pos.x = src.pos.x;
  dst.pos.y = src.pos.y;
  dst.vel.x = src.vel.x;
  dst.vel.y = src.vel.y;
  dst.facing.x = src.facing.x;
  dst.facing.y = src.facing.y;
  dst.mode = src.mode;
  dst.modeTimer = src.modeTimer;
  dst.pulled = src.pulled;
}

function makeControlSlot(): TeamControlState {
  return {
    teamId: "home",
    controlledByHuman: { P1: null, P2: null },
    lastManualSwitchTick: 0,
    manualGoalieSeat: null,
    goaliePullToggleHeldLastTick: false,
    tactic: "balanced",
  };
}

function copyControlInto(dst: TeamControlState, src: TeamControlState): void {
  dst.teamId = src.teamId;
  dst.controlledByHuman.P1 = src.controlledByHuman.P1;
  dst.controlledByHuman.P2 = src.controlledByHuman.P2;
  dst.lastManualSwitchTick = src.lastManualSwitchTick;
  dst.manualGoalieSeat = src.manualGoalieSeat;
  dst.goaliePullToggleHeldLastTick = src.goaliePullToggleHeldLastTick;
  dst.tactic = src.tactic;
}

const SNAPSHOT_PLAYERS_POOL: PlayerState[] = [];
const SNAPSHOT_GOALIES_POOL: GoalieState[] = [];
const SNAPSHOT_CONTROL_POOL: TeamControlState[] = [];

const SNAPSHOT_BUFFER = {
  tick: 0,
  clock: { periodIndex: 0 as 0 | 1 | 2, remainingTicks: 0, phase: "Faceoff" } as MatchClock,
  score: { home: 0, away: 0 } as ScoreState,
  puck: {
    pos: { x: 0, y: 0 },
    vel: { x: 0, y: 0 },
    mode: "Loose",
    ownerId: null,
    lastTouchedBy: null,
    noRecaptureUntilTick: 0,
    opponentLockoutPlayerId: null,
    opponentLockoutUntilTick: 0,
    faceoffCenterLockoutUntilTick: 0,
  } as PuckState,
  players: [] as PlayerState[],
  goalies: [] as GoalieState[],
  control: [] as TeamControlState[],
  // Per-rule scratch view. Re-allocated as a shared object once at module
  // load; sub-fields are copied in below in takeSnapshot as rule slices add
  // them. Empty for Slice -1 → snapshot.rules === {}.
  rules: {} as RulesState,
  events: [] as MatchEvent[],
  hash: 0,
};

export function takeSnapshot(state: MatchState): MatchSnapshot {
  const buf = SNAPSHOT_BUFFER;
  buf.tick = state.tick;
  buf.clock.periodIndex = state.clock.periodIndex;
  buf.clock.remainingTicks = state.clock.remainingTicks;
  buf.clock.phase = state.clock.phase;
  buf.clock.nextPlayPhase = state.clock.nextPlayPhase;
  buf.score.home = state.score.home;
  buf.score.away = state.score.away;
  buf.hash = state.hash;

  buf.puck.pos.x = state.puck.pos.x;
  buf.puck.pos.y = state.puck.pos.y;
  buf.puck.vel.x = state.puck.vel.x;
  buf.puck.vel.y = state.puck.vel.y;
  buf.puck.mode = state.puck.mode;
  buf.puck.ownerId = state.puck.ownerId;
  buf.puck.lastTouchedBy = state.puck.lastTouchedBy;
  buf.puck.noRecaptureUntilTick = state.puck.noRecaptureUntilTick;
  buf.puck.opponentLockoutPlayerId = state.puck.opponentLockoutPlayerId;
  buf.puck.opponentLockoutUntilTick = state.puck.opponentLockoutUntilTick;
  buf.puck.faceoffCenterLockoutUntilTick = state.puck.faceoffCenterLockoutUntilTick;

  while (SNAPSHOT_PLAYERS_POOL.length < state.players.length) {
    SNAPSHOT_PLAYERS_POOL.push(makePlayerSlot());
  }
  buf.players.length = state.players.length;
  for (let i = 0; i < state.players.length; i++) {
    const slot = SNAPSHOT_PLAYERS_POOL[i] as PlayerState;
    copyPlayerInto(slot, state.players[i] as PlayerState);
    buf.players[i] = slot;
  }

  while (SNAPSHOT_GOALIES_POOL.length < state.goalies.length) {
    SNAPSHOT_GOALIES_POOL.push(makeGoalieSlot());
  }
  buf.goalies.length = state.goalies.length;
  for (let i = 0; i < state.goalies.length; i++) {
    const slot = SNAPSHOT_GOALIES_POOL[i] as GoalieState;
    copyGoalieInto(slot, state.goalies[i] as GoalieState);
    buf.goalies[i] = slot;
  }

  while (SNAPSHOT_CONTROL_POOL.length < state.control.length) {
    SNAPSHOT_CONTROL_POOL.push(makeControlSlot());
  }
  buf.control.length = state.control.length;
  for (let i = 0; i < state.control.length; i++) {
    const slot = SNAPSHOT_CONTROL_POOL[i] as TeamControlState;
    copyControlInto(slot, state.control[i] as TeamControlState);
    buf.control[i] = slot;
  }

  // Rules sub-record. Empty for Slice -1; future rule slices that add a
  // sub-field also copy it into `buf.rules` here. The reference itself is
  // stable across calls (pool semantics).
  copyRulesInto(buf.rules, state.rules);

  // Events are still per-event allocations (treated as immutable). The
  // snapshot's events array is a separate, reused array of references; that
  // way drainEvents on `state` doesn't disturb the snapshot's view.
  buf.events.length = 0;
  for (const ev of state.events) buf.events.push(ev);

  return buf as unknown as MatchSnapshot;
}

function copyRulesInto(target: RulesState, source: RulesState): void {
  // Per-rule shallow copy. Each rule field is independently copied so
  // optional fields stay optional in the snapshot view.
  target.nextFaceoffSpot = source.nextFaceoffSpot;
  if (source.faceoffIntents !== undefined) {
    target.faceoffIntents = {};
    if (source.faceoffIntents.home !== undefined) {
      target.faceoffIntents.home = cloneTracker(source.faceoffIntents.home);
    }
    if (source.faceoffIntents.away !== undefined) {
      target.faceoffIntents.away = cloneTracker(source.faceoffIntents.away);
    }
  } else {
    target.faceoffIntents = undefined;
  }
  if (source.offside !== undefined) {
    target.offside = {
      team: source.offside.team,
      // Copy the ID array — snapshots are read-only and the renderer
      // would otherwise share a reference into live state.
      potentialIds: source.offside.potentialIds.slice(),
      puckEnterX: source.offside.puckEnterX,
      puckEnterY: source.offside.puckEnterY,
    };
  } else {
    target.offside = undefined;
  }
  if (source.icing !== undefined) {
    target.icing = {
      team: source.icing.team,
      shooterId: source.icing.shooterId,
      shotFromY: source.icing.shotFromY,
    };
  } else {
    target.icing = undefined;
  }
  if (source.puckOutOfPlay !== undefined) {
    target.puckOutOfPlay = { ticks: source.puckOutOfPlay.ticks };
  } else {
    target.puckOutOfPlay = undefined;
  }
  if (source.shootout !== undefined) {
    target.shootout = {
      round: source.shootout.round,
      shooterTeam: source.shootout.shooterTeam,
      shooterId: source.shootout.shooterId,
      subPhase: source.shootout.subPhase,
      subPhaseEndTick: source.shootout.subPhaseEndTick,
      lastResult: source.shootout.lastResult,
      score: { home: source.shootout.score.home, away: source.shootout.score.away },
      attempts: { home: source.shootout.attempts.home, away: source.shootout.attempts.away },
      shotTaken: source.shootout.shotTaken,
      shotTakenAtTick: source.shootout.shotTakenAtTick,
      maxPuckForward: source.shootout.maxPuckForward,
    };
  } else {
    target.shootout = undefined;
  }
}

export function drainEvents(state: MatchState): void {
  state.events.length = 0;
}

function cloneTracker(t: FaceoffIntentTracker): FaceoffIntentTracker {
  return {
    currentTarget: t.currentTarget,
    targetSetAtTick: t.targetSetAtTick,
    passHeldLastTick: t.passHeldLastTick,
    commit:
      t.commit === null
        ? null
        : {
            target: t.commit.target,
            commitTick: t.commit.commitTick,
            stabilityTicks: t.commit.stabilityTicks,
          },
  };
}
